import React, { useCallback, useState } from 'react';
import { useFormikContext } from 'formik';
import { Button, Checkbox } from '@clearcover/atlas';

import { FormInput, Divider, useToast } from 'Shared/components';
import { Asset } from 'Shared/components/Asset';
import {
  formatPhoneNumber,
  stripUSPhoneCountryCode,
  addUSPhoneCountryCode,
} from 'Shared/utils';
import { useAppContext, usePurchaseContext } from 'QuoteApp/services/providers';
import { getErrorMessage } from 'QuoteApp/utils';
import { PurchaseFormValuesType } from '../types';

import styles from './styles.module.css';

type CheckoutLinkContentProps = {
  onSwitchToDirectCheckout: () => void;
  onSent: () => void;
};

export const CheckoutLinkContent = ({
  onSwitchToDirectCheckout,
  onSent,
}: CheckoutLinkContentProps) => {
  const { toast } = useToast();
  const { details, rate } = useAppContext();
  const { sendCheckoutLink, sendCheckoutLinkInProgress } = usePurchaseContext();
  const { values } = useFormikContext<PurchaseFormValuesType>();

  const phoneFromDetails =
    details.contactInfo[details.principalInsuredId]?.phoneNumber?.value || '';

  // smsConsent is a UI-only gate: the agent must explicitly acknowledge the
  // customer's preference before the Send button is enabled. It is not
  // transmitted to the API — the BE records consent separately via the
  // customer-facing checkout flow.
  const [smsConsent, setSmsConsent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(
    stripUSPhoneCountryCode(phoneFromDetails)
  );

  const isCommandRunnable =
    details.commands.sendCheckoutLink?.runnable === true;
  const isSendDisabled =
    !smsConsent ||
    phoneNumber.length < 10 ||
    !isCommandRunnable ||
    sendCheckoutLinkInProgress;

  const handleSendCheckoutLink = useCallback(async () => {
    if (isSendDisabled) return;

    try {
      await sendCheckoutLink({
        quoteResultId: rate?.identifier ?? '',
        paymentPlanId: values.paymentPlanId,
        customerPhoneNumber: addUSPhoneCountryCode(phoneNumber),
      });

      onSent();
    } catch (error) {
      toast({
        type: 'error',
        message: getErrorMessage(error),
        withSecondaryNav: false,
        icon: 'error',
      });
    }
  }, [
    isSendDisabled,
    sendCheckoutLink,
    rate,
    values.paymentPlanId,
    phoneNumber,
    onSent,
    toast,
  ]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <Asset variant="lockOutlined" />
          <h3 className={styles.title}>Checkout-Link</h3>
        </div>
        <button
          type="button"
          className={styles.switchLink}
          onClick={onSwitchToDirectCheckout}
        >
          <Asset variant="swapHoriz" />
          <span className={styles.switchLinkText}>
            Switch to direct-checkout
          </span>
        </button>
      </div>

      <p className={styles.description}>
        To protect the security of the customer, we do not collect payment
        information from the customer directly. Customer will be asked to agree
        to the attached documents and then to complete payment.
      </p>

      <div className={styles.divider}>
        <Divider />
      </div>

      <p className={styles.consentLabel}>
        Clearcover will send a text message to their mobile number containing a
        secure link to finalize their auto insurance purchase.
      </p>

      <h4 className={styles.consentHeading}>
        Does customer consent to text communications?
      </h4>

      <Checkbox
        name="checkout-link-sms-consent"
        label="Yes, the customer accepts text communication consent"
        value={smsConsent}
        onChange={setSmsConsent}
        className={styles.smsConsent}
      />

      <div className={styles.sendRow}>
        <span className={styles.phoneLabel}>Cell phone number</span>
        <div className={styles.phoneInputWrapper}>
          <FormInput
            variant="text"
            name="checkout-link-phone"
            value={formatPhoneNumber(phoneNumber)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPhoneNumber(stripUSPhoneCountryCode(e.target.value))
            }
            maxLength={12}
            className={styles.phoneInput}
            disabled={!smsConsent}
          />
        </div>
        <Button
          variant="primary"
          size="md"
          disabled={isSendDisabled}
          isSubmitting={sendCheckoutLinkInProgress}
          onClick={handleSendCheckoutLink}
          className={styles.sendButton}
          data-testid="send-checkout-link-button"
        >
          Send Checkout-Link
        </Button>
      </div>

      <div className={styles.infoBanner}>
        <Asset variant="infoFilled" />
        <span className={styles.infoBannerText}>
          Message and data rates may apply.
        </span>
      </div>
    </div>
  );
};
